/* layout.tsx generated by @compiled/babel-plugin v0.29.1 */
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import "./layout.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
import React, { createContext, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { bind } from 'bind-event-listener';
import { useUID } from 'react-uid';
import { media } from '@atlaskit/primitives/responsive';
import { SkipLinksContainer } from '../../components/skip-links/skip-links-container';
import { useSkipLink } from '../../context/skip-links/skip-links-context';
import { SkipLinksProvider } from '../../context/skip-links/skip-links-provider';
import { DangerouslyHoistCssVarToDocumentRoot, HoistCssVarToLocalGrid } from './hoist-utils';
import { PanelSplitterProvider } from './panel-splitter';
import { SideNavVisibilityProvider, useSideNavVisibility, useToggleSideNav } from './side-nav-visibility';
import { useSideNavVisibilityCallbacks } from './side-nav/use-side-nav-visibility-callbacks';

// These are "compressed" to discourage usage.
var sideNavVar = '--n_sNvw';
var asideVar = '--n_asDw';
var panelVar = '--n_pnlW';
var bannerMountedVar = '--n_bnrM';
var topBarMountedVar = '--n_tbrM';

// We aren't using template literals here because Compiled can't compiled them in platform ATM.
var contentHeightWhenFixed = "calc(100vh - var(--n_bnrM, 0px) - var(--n_tbrM, 0px))";
var contentInsetBlockStart = "calc(var(--n_bnrM, 0px) + var(--n_tbrM, 0px))";

// We define variables as they can change their size depending on the viewport width. That isn't needed for
// general grid item slots because, well, they just take up the size of the grid item! In this instance since
// the UNSAFE absolutely positioned sibling isn't on the grid it needs extra layout information.
// Note: THESE ARE THE SAME VALUES AS THE LEGACY PAGE LAYOUT.
// WHEN WE ELIMINATE USAGE OF THOSE VARIABLES THESE CAN BE RENAMED TO BE HASHED.
var UNSAFE_topBarVar = '--topNavigationHeight';
var UNSAFE_bannerVar = '--bannerHeight';
var UNSAFE_sideNavLayoutVar = '--leftSidebarWidth';
var UNSAFE_asideLayoutVar = '--rightSidebarWidth';
var UNSAFE_panelLayoutVar = '--rightPanelWidth';

// The following UNSAFE variables are used to absolutely position elements that aren't a child of page layout.
// Known use cases: Legacy pages rendered inside Confluence and Jira.
export var UNSAFE_MAIN_BLOCK_START_FOR_LEGACY_PAGES_ONLY = "calc(var(".concat(UNSAFE_bannerVar, ", 0px) + var(").concat(UNSAFE_topBarVar, ", 0px))");
export var UNSAFE_MAIN_INLINE_START_FOR_LEGACY_PAGES_ONLY = "var(".concat(UNSAFE_sideNavLayoutVar, ", 0px)");
export var UNSAFE_MAIN_INLINE_END_FOR_LEGACY_PAGES_ONLY = "calc(var(".concat(UNSAFE_asideLayoutVar, ", 0px) + var(").concat(UNSAFE_panelLayoutVar, ", 0px))");
var panelSplitterResizingVar = '--localPanelSplitterResizingWidth';
var sideNavWidthResizeBounds = {
  min: '160px',
  max: '50vw'
};

// ID of the root element that the banner and top bar slots hoist their sizes to. Only internally exported.
export var gridRootId = 'unsafe-design-system-page-layout-root';

// useUID generates a number which cannot be used as an Id for html elements as is, therefore we need to prefix it with text.
// TODO: Change to useId when we switch to React 18
export var usePrefixedUID = function usePrefixedUID() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'element';
  return "".concat(prefix, "-").concat(useUID());
};

/**
 * We define the z-indexes here so each page slot can be locally layered against each other.
 * For globally defined values such as flag, modal, etc, we can continue to
 * rely on accessing them through global means.
 */
var localSlotLayers = {
  topBar: 4,
  banner: 4,
  mainBorder: 3,
  panel: 2,
  sideNav: 1
};
var styles = {
  root: "_1e0c11p5 _1tke1kxc _1lmcq9em _yv0ei47z _2z0516ab _1ciragmp _12fkuz0r _12qzrxre _1rqt70if _xkmgbaui _jbc7rxre _tyve1jg8",
  banner: "_nd5ldkfm _4t3iutvi _152tidpf _kqsw1if8 _1pbyegat _1reo15vq _18m915vq",
  topBar: "_1e0c11p5 _yv0e1mfv _18zrutpp _zulppxbi _4cvr1h6o _bfhkvuon _vchhusvi _nd5l8cbt _4t3i2mmk _152t1nws _kqsw1if8 _1pbyegat _d6vu1bgi",
  sideNav: "_bfhk1bhr _16qs1cd0 _nd5l1b6c _4t3i1run _152t1l7u _kqsw1if8 _1bsb1ego _1pbykb7n _14b515li _4ap3vuon _scbpglyw _qilnk0mc",
  sideNavHiddenMobileAndDesktop: "_1e0cglyw",
  sideNavHiddenMobileOnly: "_1e0cglyw _dm2518uv",
  sideNavHiddenDesktopOnly: "_dm25glyw",
  main: "_nd5l1gzg",
  mainCurve: "_1orwi2wt",
  mainBorder: "_kqsw1if8 _nd5l1gzg _f0zu1mrw _vchhusvi _152t1l7u _mqm2zgxb _lcxvglyw _1pby11wp _165t1run",
  mainBorderCurve: "_1w6ezgxb _1orwi2wt",
  aside: "_nd5lns35 _kqswpfqs _glte12il _ndwch9n0",
  panel: "_bfhk1bhr _16qs1cd0 _nd5l1b6c _10fph9n0 _1bsb1g7f _1pbycs5v _1j46vuon _ehorzgxb _1sjdglyw _1dhy1c6w",
  fixedContentArea: "_152t1l7u _1reo1wug _18m91wug _kqsw1if8 _165t1run"
};
var DangerouslyHoistSlotSizes = /*#__PURE__*/createContext(false);

/**
 * A root component of the navigation system. It wraps the undelying components with the necessary contexts allowing to use certain data and hooks
 * @param skipLinksLabel - The very first element of the layout is a skip links container that can be accessed by pressing Tab button and holds the links to the other sections of the layout thus improving accessibility. This parameter defines the header text for this container
 */
export function Root(_ref) {
  var children = _ref.children,
    xcss = _ref.xcss,
    _ref$UNSAFE_dangerous = _ref.UNSAFE_dangerouslyHoistSlotSizes,
    UNSAFE_dangerouslyHoistSlotSizes = _ref$UNSAFE_dangerous === void 0 ? false : _ref$UNSAFE_dangerous,
    _ref$skipLinksLabel = _ref.skipLinksLabel,
    skipLinksLabel = _ref$skipLinksLabel === void 0 ? 'Skip to:' : _ref$skipLinksLabel,
    testId = _ref.testId;
  var ref = useRef(null);
  useEffect(function () {
    if (process.env.NODE_ENV !== 'production') {
      var IGNORED_ELEMENTS = ['SCRIPT', 'STYLE'];
      if (ref.current) {
        Array.from(ref.current.children).forEach(function (child) {
          if (!IGNORED_ELEMENTS.includes(child.tagName) && !child.hasAttribute('data-layout-slot')) {
            // eslint-disable-next-line no-console
            console.error("Page Layout Error\n\nThis element has been forcibly hidden:\n\n", child, "\n\nAn element was rendered as a child of the page layout root that isn't a page layout component! Resolve this error by moving it into a page layout component.\n\nThis message will not be displayed in production.\n");
          }
        });
      }
    }
  }, []);
  return /*#__PURE__*/React.createElement(SideNavVisibilityProvider, null, /*#__PURE__*/React.createElement(DangerouslyHoistSlotSizes.Provider, {
    value: UNSAFE_dangerouslyHoistSlotSizes
  }, /*#__PURE__*/React.createElement(SkipLinksProvider, null, /*#__PURE__*/React.createElement(SkipLinksContainer, {
    label: skipLinksLabel
  }), /*#__PURE__*/React.createElement("div", {
    ref: ref,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: ax([styles.root, xcss]),
    id: gridRootId,
    "data-testid": testId
  }, children))));
}
export function Banner(_ref2) {
  var children = _ref2.children,
    xcss = _ref2.xcss,
    _ref2$UNSAFE_height = _ref2.UNSAFE_height,
    UNSAFE_height = _ref2$UNSAFE_height === void 0 ? 64 : _ref2$UNSAFE_height,
    _ref2$label = _ref2.label,
    label = _ref2$label === void 0 ? 'Banner' : _ref2$label,
    testId = _ref2.testId,
    id = _ref2.id;
  var dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  useSkipLink(CID, label);
  return /*#__PURE__*/React.createElement("div", {
    id: CID,
    role: "banner",
    "data-layout-slot": true,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: ax([styles.banner, xcss]),
    "data-testid": testId
  }, /*#__PURE__*/React.createElement(HoistCssVarToLocalGrid, {
    variableName: bannerMountedVar,
    value: UNSAFE_height
  }), dangerouslyHoistSlotSizes &&
  /*#__PURE__*/
  // ------ START UNSAFE STYLES ------
  // These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
  // When they aren't needed anymore we can delete them wholesale.
  React.createElement(DangerouslyHoistCssVarToDocumentRoot, {
    variableName: UNSAFE_bannerVar,
    value: UNSAFE_height
  })
  // ------ END UNSAFE STYLES ------
  , children);
}
export function TopBar(_ref3) {
  var children = _ref3.children,
    xcss = _ref3.xcss,
    _ref3$UNSAFE_height = _ref3.UNSAFE_height,
    UNSAFE_height = _ref3$UNSAFE_height === void 0 ? 48 : _ref3$UNSAFE_height,
    _ref3$label = _ref3.label,
    label = _ref3$label === void 0 ? 'Top Bar' : _ref3$label,
    testId = _ref3.testId,
    id = _ref3.id;
  var dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  useSkipLink(CID, label);
  return /*#__PURE__*/React.createElement("header", {
    id: CID,
    "data-layout-slot": true,
    className: ax([styles.topBar, xcss]),
    "data-testid": testId
  }, /*#__PURE__*/React.createElement(HoistCssVarToLocalGrid, {
    variableName: topBarMountedVar,
    value: UNSAFE_height
  }), dangerouslyHoistSlotSizes &&
  /*#__PURE__*/
  // ------ START UNSAFE STYLES ------
  // These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
  // When they aren't needed anymore we can delete them wholesale.
  React.createElement(DangerouslyHoistCssVarToDocumentRoot, {
    variableName: UNSAFE_topBarVar,
    value: UNSAFE_height
  })
  // ------ END UNSAFE STYLES ------
  , children);
}
export function Panel(_ref4) {
  var children = _ref4.children,
    _ref4$defaultWidth = _ref4.defaultWidth,
    defaultWidth = _ref4$defaultWidth === void 0 ? 365 : _ref4$defaultWidth,
    _ref4$label = _ref4.label,
    label = _ref4$label === void 0 ? 'Panel' : _ref4$label,
    testId = _ref4.testId,
    id = _ref4.id;
  var dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  useSkipLink(CID, label);
  return /*#__PURE__*/React.createElement("section", {
    id: CID,
    "data-layout-slot": true,
    "aria-label": label,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
    style: _defineProperty({}, panelVar, "".concat(defaultWidth, "px")),
    "data-testid": testId,
    className: ax([styles.panel, styles.fixedContentArea])
  }, dangerouslyHoistSlotSizes &&
  /*#__PURE__*/
  // ------ START UNSAFE STYLES ------
  // These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
  // When they aren't needed anymore we can delete them wholesale.
  React.createElement(DangerouslyHoistCssVarToDocumentRoot, {
    variableName: UNSAFE_panelLayoutVar,
    value: "0px",
    mediaQuery: media.above.lg,
    responsiveValue: defaultWidth
  })
  // ------ END UNSAFE STYLES ------
  , children);
}
var MainStickyContext = /*#__PURE__*/createContext(false);
export function Main(_ref6) {
  var children = _ref6.children,
    isFixed = _ref6.isFixed,
    xcss = _ref6.xcss,
    _ref6$label = _ref6.label,
    label = _ref6$label === void 0 ? 'Main Content' : _ref6$label,
    testId = _ref6.testId,
    id = _ref6.id;
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  var _useSideNavVisibility = useSideNavVisibility(),
    visibleOnDesktop = _useSideNavVisibility.visibleOnDesktop;
  useSkipLink(CID, label);
  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    "data-layout-slot": true,
    className: ax([styles.mainBorder, visibleOnDesktop && styles.mainBorderCurve])
  }), /*#__PURE__*/React.createElement("div", {
    id: CID,
    "data-layout-slot": true,
    "aria-label": label
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: ax([styles.main, visibleOnDesktop && styles.mainCurve, isFixed && styles.fixedContentArea, xcss]),
    role: "main",
    "data-testid": testId
  }, /*#__PURE__*/React.createElement(MainStickyContext.Provider, {
    value: Boolean(isFixed)
  }, children)));
}
var stickyHeaderStyles = {
  root: "_kqsw1if8",
  stickyInMain: "_152tidpf",
  stickyInBody: "_152t1l7u"
};
export function MainStickyHeader(_ref7) {
  var children = _ref7.children,
    xcss = _ref7.xcss,
    testId = _ref7.testId;
  var isMainFixed = useContext(MainStickyContext);
  return /*#__PURE__*/React.createElement("div", {
    "data-testid": testId
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
    ,
    className: ax([stickyHeaderStyles.root, isMainFixed && stickyHeaderStyles.stickyInMain, !isMainFixed && stickyHeaderStyles.stickyInBody, xcss])
  }, children);
}
export function SideNav(_ref8) {
  var children = _ref8.children,
    defaultCollapsed = _ref8.defaultCollapsed,
    _ref8$defaultWidth = _ref8.defaultWidth,
    defaultWidth = _ref8$defaultWidth === void 0 ? 320 : _ref8$defaultWidth,
    testId = _ref8.testId,
    _ref8$label = _ref8.label,
    label = _ref8$label === void 0 ? 'Side Navigation' : _ref8$label,
    collapseButton = _ref8.collapseButton,
    onExpand = _ref8.onExpand,
    onCollapse = _ref8.onCollapse,
    id = _ref8.id;
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  useSkipLink(CID, label);
  var _useSideNavVisibility2 = useSideNavVisibility({
      defaultCollapsed: defaultCollapsed
    }),
    visibleOnDesktop = _useSideNavVisibility2.visibleOnDesktop,
    visibleOnMobile = _useSideNavVisibility2.visibleOnMobile,
    setVisibleOnDesktop = _useSideNavVisibility2.setVisibleOnDesktop;
  var _useState = useState(defaultWidth),
    _useState2 = _slicedToArray(_useState, 2),
    width = _useState2[0],
    setWidth = _useState2[1];
  var dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
  var panelSplitterParentRef = useRef(null);
  var sideNavVariableWidth = "clamp(".concat(sideNavWidthResizeBounds.min, ", ").concat(width, "px, ").concat(sideNavWidthResizeBounds.max, ")");
  var devTimeOnlyAttributes = {};

  // Sync the visibility in context with the local state value after SSR hydration, as it contains the default value from props
  useEffect(function () {
    setVisibleOnDesktop(visibleOnDesktop);
  }, [setVisibleOnDesktop, visibleOnDesktop]);
  useSideNavVisibilityCallbacks({
    onExpand: onExpand,
    onCollapse: onCollapse,
    visibleOnDesktop: visibleOnDesktop,
    visibleOnMobile: visibleOnMobile
  });
  if (process.env.NODE_ENV !== 'production') {
    var visible = [];
    if (visibleOnMobile) {
      visible.push('small');
    }
    if (visibleOnDesktop) {
      visible.push('large');
    }
    devTimeOnlyAttributes['data-visible'] = visible.length ? visible.join(',') : 'false';
  }
  var toggleVisibility = useToggleSideNav();
  useEffect(function () {
    var mediaQueryList = window.matchMedia('(min-width: 64rem)');
    return bind(mediaQueryList, {
      type: 'change',
      listener: function listener() {
        if (mediaQueryList.matches) {
          // We're transitioning from tablet to desktop viewport size.
          // We forcibly show the side nav if it was shown on mobile.
          if (visibleOnMobile && !visibleOnDesktop) {
            toggleVisibility();
          }
        }
      }
    });
  }, [toggleVisibility, visibleOnDesktop, visibleOnMobile]);
  return /*#__PURE__*/React.createElement("nav", _extends({
    id: CID
  }, devTimeOnlyAttributes, {
    "data-layout-slot": true,
    "aria-label": label
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: _defineProperty({}, sideNavVar, sideNavVariableWidth),
    ref: panelSplitterParentRef,
    "data-testid": testId,
    className: ax([styles.sideNav, !visibleOnMobile && visibleOnDesktop && styles.sideNavHiddenMobileOnly, visibleOnMobile && !visibleOnDesktop && styles.sideNavHiddenDesktopOnly, !visibleOnMobile && !visibleOnDesktop && styles.sideNavHiddenMobileAndDesktop])
  }), dangerouslyHoistSlotSizes &&
  /*#__PURE__*/
  // ------ START UNSAFE STYLES ------
  // These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
  // When they aren't needed anymore we can delete them wholesale.
  React.createElement(DangerouslyHoistCssVarToDocumentRoot, {
    variableName: UNSAFE_sideNavLayoutVar,
    value: "0px",
    mediaQuery: media.above.md,
    responsiveValue: visibleOnDesktop ? sideNavVariableWidth : 0
  })
  // ------ END UNSAFE STYLES ------
  , /*#__PURE__*/React.createElement(PanelSplitterProvider, {
    panelRef: panelSplitterParentRef,
    panelWidth: width,
    onCompleteResize: setWidth,
    onClick: toggleVisibility,
    resizeBounds: sideNavWidthResizeBounds,
    resizingCssVar: panelSplitterResizingVar
  }, collapseButton, children));
}
export function Aside(_ref10) {
  var children = _ref10.children,
    xcss = _ref10.xcss,
    isFixed = _ref10.isFixed,
    _ref10$defaultWidth = _ref10.defaultWidth,
    defaultWidth = _ref10$defaultWidth === void 0 ? 330 : _ref10$defaultWidth,
    _ref10$label = _ref10.label,
    label = _ref10$label === void 0 ? 'Aside' : _ref10$label,
    testId = _ref10.testId,
    id = _ref10.id;
  var dangerouslyHoistSlotSizes = useContext(DangerouslyHoistSlotSizes);
  var UID = usePrefixedUID();
  var CID = id ? id : UID;
  useSkipLink(CID, label);
  return /*#__PURE__*/React.createElement("aside", {
    id: CID,
    "data-layout-slot": true,
    "aria-label": label,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    className: ax([styles.aside, isFixed && styles.fixedContentArea, xcss])
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
    ,
    style: _defineProperty({}, asideVar, "".concat(defaultWidth, "px")),
    "data-testid": testId
  }, dangerouslyHoistSlotSizes &&
  /*#__PURE__*/
  // ------ START UNSAFE STYLES ------
  // These styles are only needed for the UNSAFE legacy use case for Jira + Confluence.
  // When they aren't needed anymore we can delete them wholesale.
  React.createElement(DangerouslyHoistCssVarToDocumentRoot, {
    variableName: UNSAFE_asideLayoutVar,
    value: "0px",
    mediaQuery: media.above.md,
    responsiveValue: defaultWidth
  })
  // ------ END UNSAFE STYLES ------
  , children);
}